import moment from 'moment';
import React from 'react';
import { generatePath, RouteComponentProps, withRouter } from 'react-router';
import {
  completeCarePlanActivity,
  updateCarePlan,
} from '../../../apis/carePlan';
import { updateConditionsByPatientId } from '../../../apis/conditions';
import { getQuestionnaires } from '../../../apis/questionnaire';
import { DEFAULT_QUESTIONNAIRES } from '../../../constants';
import CarePlanProvider from '../../../containers/Providers/CarePlan';
import CristalProvider from '../../../containers/Providers/Cristal';
import OrganizationProvider from '../../../containers/Providers/Organizations';
import PatientProvider from '../../../containers/Providers/Patient';
import QuestionnaireResponseProvider from '../../../containers/Providers/QuestionnnaireResponse';
import Questionnaire from '../../../containers/Questionnaires/Questionnaire';
import { useCarePlan } from '../../../contexts/carePlan';
import { useCristal } from '../../../contexts/cristal';
import { useOrganization } from '../../../contexts/organization';
import { usePatient } from '../../../contexts/patient';
import { usePractitioner } from '../../../contexts/practitioner';
import { useQuestionnaireReponse } from '../../../contexts/questionnaireResponse';
import { getQuestionnaireByCondition } from '../../../modules/unsw-care-plan/eolCarePlan';
import { calculateCriSTALScore } from '../../../modules/unsw-cristal';
import { CristalConditionsGenerator } from '../../../modules/unsw-fhir-conditions';
import QuestionnaireMutator from '../../../modules/unsw-fhir-questionnaire/Utilities/QuestionnaireMutator';
import { ICarePlan } from '../../../modules/unsw-fhir/CarePlan';
import { IPatient } from '../../../modules/unsw-fhir/Patient';
import { IPractitioner } from '../../../modules/unsw-fhir/Practitioner';
import { IQuestionnaire } from '../../../modules/unsw-fhir/Questionnaire';
import { IQuestionnaireResponse } from '../../../modules/unsw-fhir/QuestionnaireResponse';

async function processCristalConditions(
  response: IQuestionnaireResponse,
  qtnnaire: IQuestionnaire,
  patient: IPatient,
  practitioner: IPractitioner,
) {
  const conditions = new CristalConditionsGenerator(
    qtnnaire,
  ).generateConditions(response, patient!, {
    id: practitioner.id,
    resourceType: 'Practitioner',
  });
  return await updateConditionsByPatientId(conditions, patient!.id!);
}

const processCristal = async (
  carePlan: ICarePlan,
  response: IQuestionnaireResponse | undefined,
  qtnnaire: IQuestionnaire,
  patient: IPatient,
  practitioner: IPractitioner,
) => {
  if (qtnnaire.name !== 'cristal' || response === undefined) return carePlan;
  const conditions = await processCristalConditions(
    response,
    qtnnaire,
    patient,
    practitioner,
  );
  const questionnaires = await getQuestionnaires();
  try {
    if (questionnaires.length === 0)
      throw new Error('This should never be empty');
    const requiredQuestionnaires = [
      ...questionnaires.filter(q =>
        DEFAULT_QUESTIONNAIRES.some(dQ => dQ === q.name),
      ),
      ...conditions
        .map(
          condition =>
            condition.clinicalStatus === 'active' &&
            getQuestionnaireByCondition(questionnaires, condition),
        )
        .filter((q): q is IQuestionnaire => !!q),
    ];
    return await updateCarePlan(carePlan, requiredQuestionnaires, conditions);
  } catch (e) {
    console.error(e);
    return carePlan;
  }
};

const QuestionnaireRenderComponent = ({
  onComplete,
}: {
  onComplete: () => void;
}) => {
  const { patient } = usePatient();
  const { practitioner } = usePractitioner();
  const { organization } = useOrganization();
  const { carePlan, setCarePlan } = useCarePlan();
  const { setScore } = useCristal();
  const {
    questionnaire,
    questionnaireResponse,
    questionnaireResponseLoading,
    setQuestionnaireResponse,
  } = useQuestionnaireReponse();
  if (
    !patient ||
    !patient.id ||
    !practitioner ||
    !practitioner.id ||
    !organization ||
    !organization.id ||
    !carePlan
  )
    return null;
  return (
    <Questionnaire
      questionnaire={questionnaire}
      questionnaireResponse={questionnaireResponse}
      loading={questionnaireResponseLoading}
      setQuestionnaireResponse={setQuestionnaireResponse}
      onComplete={async (qtnnaire, response) => {
        if (!response) return;
        let mCarePlan = await processCristal(
          carePlan,
          response,
          qtnnaire,
          patient,
          practitioner,
        );
        if (mCarePlan || carePlan)
          mCarePlan = await completeCarePlanActivity(
            mCarePlan || carePlan,
            qtnnaire.id!,
            response.id!,
          );
        await setCarePlan(mCarePlan || carePlan);
        onComplete();
      }}
      beforeSave={async (response, qtniare) => {
        if (qtniare.name === 'cristal' && response) {
          let lResponse: IQuestionnaireResponse | undefined = response;
          const mutator = new QuestionnaireMutator(qtniare, response);

          if (patient && patient.birthDate) {
            const age = moment().diff(patient.birthDate, 'years', false);
            if (age >= 65) {
              lResponse = mutator.addResponse({
                '8-1': 'true',
              });
              mutator.updateResponse(lResponse);
            }
          }
          if (!lResponse) return lResponse;
          const score = calculateCriSTALScore(lResponse);
          setScore(score);
          return (
            mutator.addResponse({
              '8-0': score.toString(),
            }) || undefined
          );
        }
        return response;
      }}
      patientId={patient && patient.id}
      authorId={practitioner.id || ''}
    />
  );
};

const QuestionnaireEdit = (
  props: RouteComponentProps<{
    patientId: string;
    questionnaireResponseId: string;
    organizationId: string;
  }>,
) => {
  const { patientId, questionnaireResponseId, organizationId } =
    props.match.params;
  const patientPath = generatePath(
    '/organization/:organizationId/patient/:patientId/',
    props.match.params,
  );
  return (
    <OrganizationProvider organizationId={organizationId}>
      <PatientProvider patientId={patientId}>
        <CarePlanProvider patientId={patientId}>
          <CristalProvider patientId={patientId}>
            <QuestionnaireResponseProvider
              questionnaireResponseId={questionnaireResponseId}>
              <QuestionnaireRenderComponent
                onComplete={() => props.history.push(patientPath)}
              />
            </QuestionnaireResponseProvider>
          </CristalProvider>
        </CarePlanProvider>
      </PatientProvider>
    </OrganizationProvider>
  );
};
export default withRouter(QuestionnaireEdit);
